import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  Accordion,
  Grid,
  Segment,
  Button
} from 'semantic-ui-react';

import {
  AC_OWNER_TYPE,
  PAGES
} from '_constants';

import {
  getACLicenseExpiresOnLabel,
  getACLicenseOwnerType,
  getACLicenseStatusLabel,
  getACLicenseTypeLabel,
  getACLicensePoolLabel
} from '_utils/activationCodes';

import {
  getShortSublicensesLabel
} from '_utils/licenses';

import {
  licensePoolPropType,
  getIsActiveAndNotExpiredLicensePool
} from '_utils/licensePools';

import { STATUS } from '../../../server/constants';

import ACLicenseSublicenses from './sublicenses';
import ACLicenseDeactivationInfo from './deactivationInfo';
import ACLicenseActivationInfo from './activationInfo';
import ACLicensePoolInfo from "./licensePoolInfo";
import LineItem from '_views/lineItem';
import TransferInstACLicenseModal from './transferInstACLicenseModal';

const ActivationCodeItem = ({
  canNavigateUsers,
  canAdminLicense,
  license,
  owner,
  onDeactivate,
  onUpgrade,
  onTransfer,
  setNavigation
}) => {

  const onNavigateToUser = user => {
    if (!canNavigateUsers) return;
    setNavigation({ pageName: PAGES.USERS, ids: [user._id] });
  };

  const ownerType = getACLicenseOwnerType(license);

  const panels = [
    {
      title: `Add Ons ${getShortSublicensesLabel(license.sublicenses || [])}`,
      content: {
        content: <ACLicenseSublicenses sublicenses={ license.sublicenses || [] } />,
        key: `ac_${license.activated_info.ac_id}_addons`
      }
    }
  ]

  if (ownerType === AC_OWNER_TYPE.INSTITUTION) {
    panels.push({
      title: `License Pool: ${getACLicensePoolLabel(license)}`,
      content: {
        content: <ACLicensePoolInfo licensePool={ license.license_pool } />,
        key: `ac_${license.activated_info.ac_id}_pool`
      }
    })
  }

  if (license.status === STATUS.NOT_ACTIVE) {
    panels.push({
      title: `Deactivaton Info`,
      content: {
        content: <ACLicenseDeactivationInfo license={ license } onNavigateToUser={ onNavigateToUser } />,
        key: `ac_${license.activated_info.ac_id}_deact`
      }
    });
  }

  const onDeactivateLicense = () => {
    if (window.confirm('Do you want to deactivate this AC License? Doing so will unauthorize the end user.')) {
      onDeactivate(license);
    }
  };

  const onUpgradeLicense = () => {
    if (window.confirm(`Do you want to upgrade the ${license.name} AC License with AC ID ${license.activated_info.ac_id}?`)) {
      onUpgrade(license);
    }
  };

  const isUpgradable = ownerType === AC_OWNER_TYPE.RESELLER && license.name !== 'max9' && onUpgrade;
  const canBeTransferred = ownerType === AC_OWNER_TYPE.INSTITUTION && onTransfer &&
    owner && owner.license_pools && owner.license_pools.filter(p => p._id !== license.license_pool_id && getIsActiveAndNotExpiredLicensePool(p)).length >= 1;

  return (
    <Segment tertiary= { license.status === STATUS.NOT_ACTIVE || moment(license.expires_on).isBefore(moment()) } >
      <Grid>
        <Grid.Row columns={ 2 } >
          <Grid.Column width={ 8 } >
            <LineItem label='Activation Code' text={ license.activated_info.ac_id } />
            <LineItem label='Type' text={ getACLicenseTypeLabel(license) } />
            <LineItem label='Created On' text={ license.activated_on } isDate />
            <LineItem label='Expires On' text={ getACLicenseExpiresOnLabel(license) } />
            <LineItem label='Status' text={ getACLicenseStatusLabel(license) } />
            <LineItem label='License Count' text={ license.activated_info.license_count } />
            <LineItem label='Upgrade' text={ license.activated_info.upgrade || false } disable={ ownerType === AC_OWNER_TYPE.INSTITUTION } />
            <LineItem label='Description' text={ license.description || '' } />
            <br/>
            {
              canAdminLicense && license.status === STATUS.ACTIVE ? (
                <Button.Group>
                  <Button negative compact onClick={ onDeactivateLicense } >Deactivate</Button>
                  {
                    isUpgradable ? <Button positive compact onClick= { onUpgradeLicense }>Upgrade to Max 9</Button> : null
                  }
                  {
                    canBeTransferred ? <TransferInstACLicenseModal onTransfer={ onTransfer } institution={ owner } license={ license } /> : null
                  }
                </Button.Group>
              ) : null
            }
          </Grid.Column>
          <Grid.Column width={ 8 } >
            <Segment>
              <ACLicenseActivationInfo license={ license } onNavigateToUser={ onNavigateToUser } />
            </Segment>
            <Accordion fluid styled panels={ panels } />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
}

ActivationCodeItem.propTypes = {
  canNavigateUsers: PropTypes.bool.isRequired,
  canAdminLicense: PropTypes.bool.isRequired,
  setNavigation: PropTypes.func.isRequired,
  license: PropTypes.object.isRequired,
  owner: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    license_pools: PropTypes.arrayOf(licensePoolPropType)
  }).isRequired, // the institution or reseller
  onUpgrade: PropTypes.func, // only available for C74Admins and Resellers
  onDeactivate: PropTypes.func.isRequired,
  onTransfer: PropTypes.func
}

export default ActivationCodeItem



// WEBPACK FOOTER //
// ./webAdmin/views/activationCodes/item.js